import { Button, cn } from "@heroui/react";

import { OpenMode, useLocalStorageForm } from "@onesolution/components";
import {
	BOARDS_KEY_MAPPER_ENUM,
	SERVICES_COLUMNS,
	USERS_COLUMNS,
	USER_ROLES,
} from "@onesolution/constants";
import { type AppFormApi, useLanguage } from "@onesolution/hooks";
import {
	getItemPageByValuesFromServer,
	getStatusChoicesFromServer,
} from "@onesolution/serverFn";
import {
	type UserById,
	createUserOnServer,
	updateUserOnServer,
} from "@onesolution/serverFn/user";
import { useQueryClient } from "@tanstack/react-query";
import ServicesSelector from "../form/Selectors/ServicesSelector";

const activeOptions = [
	{ key: "true", name: "active" },
	{ key: "false", name: "inactive" },
];
const UsersRoles = Object.values(USER_ROLES).map((role) => ({
	name: role,
	key: role,
}));

export default function UserForm({
	onClose,
	mode,
	loadedData,
}: {
	onClose: () => void;
	mode: OpenMode;
	loadedData?: UserById;
}) {
	const { t } = useLanguage();
	const queryClient = useQueryClient();
	const services = getItemPageByValuesFromServer.useQuery({
		boardId: BOARDS_KEY_MAPPER_ENUM.services,
		columnIds: [SERVICES_COLUMNS.name],
		filter: [],
	});
	const languagesQuery = getStatusChoicesFromServer.useQuery({
		boardId: BOARDS_KEY_MAPPER_ENUM.users,
		columnId: USERS_COLUMNS.language,
	});
	const createUser = createUserOnServer.useMutation();
	const updateUser = updateUserOnServer.useMutation();
	const mutationMapping = {
		[OpenMode.creation]: createUser,
		[OpenMode.edition]: updateUser,
	};
	const isMutationLoading = updateUser.isPending || createUser.isPending;

	const form = useLocalStorageForm<
		Omit<UserById, "active"> & {
			active: string;
		}
	>({
		localStorageKey: "@onevision-users",
		mode,
		loadedData: { ...loadedData, active: loadedData?.active.toString() },
		defaultValues: {
			firstname: "",
			email: "",
			lastname: "",
			role: "Member",
			language: "FR",
			active: "true",
			services: [],
			favoriteServices: [],
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["infinite", "users"],
			});
			return onClose();
		},
		onSubmitMutation: async ({ value }, mutationOpts) => {
			if (mode === OpenMode.close || mode === OpenMode.details) return;
			return mutationMapping[mode].mutate(
				{
					...value,
					active: value.active === "true",
					id: loadedData?.id,
				},
				mutationOpts(),
			);
		},
	});

	return (
		<form
			className="flex gap-4 flex-col w-96"
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.handleSubmit();
			}}
		>
			<div className="flex justify-between gap-4">
				<form.AppField name="lastname">
					{(field) => <field.Input isRequired label={t("basic.lastname")} />}
				</form.AppField>
				<form.AppField name="firstname">
					{(field) => <field.Input isRequired label={t("basic.firstname")} />}
				</form.AppField>
			</div>
			<form.AppField name="email">
				{(field) => (
					<field.Input
						isRequired
						disabled={mode === OpenMode.edition}
						label={t("name.email")}
					/>
				)}
			</form.AppField>
			<div
				className={cn(
					"flex justify-between gap-4",
					mode === OpenMode.details && "flex-col",
				)}
			>
				<form.AppField name="role">
					{(field) => (
						<field.Select
							isRequired
							items={UsersRoles}
							label={t("basic.role")}
						/>
					)}
				</form.AppField>
				<ServicesSelector
					name="services"
					form={form as AppFormApi}
					onOpenChange={() => {
						const { favoriteServices, services } = form.state.values;
						const newFavorites = favoriteServices.filter((s) =>
							services.includes(s),
						);
						if (newFavorites.length !== favoriteServices.length)
							form.setFieldValue("favoriteServices", newFavorites);
					}}
				/>
			</div>
			<ServicesSelector
				label={t("basic.favorite_services")}
				name="favoriteServices"
				items={(form.getFieldValue("services") || []).map(
					(id) => services.data?.find((s) => s.id === id)!,
				)}
				form={form as AppFormApi}
			/>
			<div
				className={cn(
					"flex justify-between gap-4",
					mode === OpenMode.details && "flex-col",
				)}
			>
				<form.AppField name="language">
					{(field) => (
						<field.Select
							isRequired
							isLoading={languagesQuery.isFetching}
							items={languagesQuery.data || []}
							label={t("basic.language")}
						/>
					)}
				</form.AppField>
				<form.AppField name="active">
					{(field) => (
						<field.Select
							isRequired
							items={activeOptions}
							label={t("basic.active")}
						/>
					)}
				</form.AppField>
			</div>
			{mode !== OpenMode.details && (
				<form.Subscribe
					selector={(state) => [state.canSubmit, state.isSubmitting]}
				>
					{([canSubmit]) => (
						<div className="flex gap-2">
							<Button
								type="submit"
								color="primary"
								isLoading={isMutationLoading}
								isDisabled={!canSubmit || isMutationLoading}
								className="max-w-min"
							>
								{mode === OpenMode.creation
									? t("basic.create")
									: t("basic.save")}
							</Button>
						</div>
					)}
				</form.Subscribe>
			)}
		</form>
	);
}
